import { BASE_URL } from 'config';
import React, { useEffect, useState } from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import CircularProgress from "@mui/material/CircularProgress";
import Avatar from "@mui/material/Avatar";
import IconButton from "@mui/material/IconButton";
import PhotoCamera from "@mui/icons-material/PhotoCamera";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import MenuItem from "@mui/material/MenuItem";
import Autocomplete from "@mui/material/Autocomplete";
import Swal from "sweetalert2";
import axios from "axios";

const StoreInfoEdit = ({ open, onClose }) => {
  const [loading, setLoading] = useState(false);
  const [store, setStore] = useState(null);
  const [form, setForm] = useState({
    name_ar: "",
    name_en: "",
    email: "",
    business_activity: "",
    market: "",
    country: "",
    state: "",
    city: "",
    logo: "",
    image: "",
    location: "",
    street: "",
    zip_code: "",
    subcode: "",
  });
  const [logoPreview, setLogoPreview] = useState("");
  const [imagePreview, setImagePreview] = useState("");
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [locations, setLocations] = useState([]);
  const [statesOptions, setStatesOptions] = useState([]);
  const [citiesOptions, setCitiesOptions] = useState([]);
  const [marketsOptions, setMarketsOptions] = useState([]);
  const [businessActivities, setBusinessActivities] = useState([]);

  useEffect(() => {
    if (open) {
      fetchStore();
      fetchLocations();
      setErrors({});
      setTouched({});
    }
    // eslint-disable-next-line
  }, [open]);

  // دالة توليد القوائم التابعة بناءً على القيم الحالية
  const buildLocationOptions = (countryName, stateName, cityName) => {
    let states = [];
    let cities = [];
    let markets = [];
    const countryObj = locations.find((c) => c.name === countryName);
    if (countryObj) {
      states = countryObj.childrens || [];
      const stateObj = states.find((s) => s.name === stateName);
      if (stateObj) {
        cities = stateObj.childrens || [];
        const cityObj = cities.find((ct) => ct.name === cityName);
        if (cityObj) {
          markets = cityObj.markets || [];
        }
      }
    }
    setStatesOptions(states);
    setCitiesOptions(cities);
    setMarketsOptions(markets);
  };

  // تعبئة الحقول المرتبطة بالمواقع بعد جلب بيانات المتجر وبيانات المواقع
  useEffect(() => {
    if (!store || locations.length === 0) return;
    // تعبئة الدولة والولاية والمدينة
    const countryName = store.location_hierarchy?.country?.name || "";
    const stateName = store.location_hierarchy?.state?.name || "";
    const cityName = store.location_hierarchy?.province?.name || "";
    // تعبئة السوق
    let marketName = "";
    if (countryName && stateName && cityName) {
      const countryObj = locations.find((c) => c.name === countryName);
      const stateObj = countryObj?.childrens?.find((s) => s.name === stateName);
      const cityObj = stateObj?.childrens?.find((ct) => ct.name === cityName);
      if (cityObj && store.market_id) {
        const marketObj = cityObj.markets?.find((m) => m.id === store.market_id);
        marketName = marketObj?.name || "";
      }
    }
    // تعبئة النشاط التجاري
    let businessActivityName = "";
    if (store.business_activitie_id && businessActivities.length > 0) {
      const actObj = businessActivities.find((a) => a.id === store.business_activitie_id);
      businessActivityName = actObj?.name || "";
    }
    setForm((prev) => ({
      ...prev,
      country: countryName,
      state: stateName,
      city: cityName,
      market: marketName,
      business_activity: businessActivityName,
      // بقية الحقول كما هي
      name_ar: store.name_ar || "",
      name_en: store.name_en || "",
      email: store.email || "",
      phone: store.phone || "",
      logo: store.logo || "",
      image: store.image || "",
      location: store.location || "",
      street: store.street || "",
      zip_code: store.zip_code || "",
      subcode: store.subcode || "",
    }));
    // منطق عرض الشعار
    if (store.logo && typeof store.logo === "string") {
      setLogoPreview(
        store.logo.startsWith("http")
          ? store.logo
          : `https://back.al-balad.sa/uploads/stores/logos/${store.logo}`
      );
    } else {
      setLogoPreview("");
    }
    // صورة المتجر
    if (store.image && typeof store.image === "string") {
      setImagePreview(
        store.image.startsWith("http")
          ? store.image
          : `https://back.al-balad.sa/uploads/stores/images/${store.image}`
      );
    } else {
      setImagePreview("");
    }
    // توليد القوائم التابعة مباشرة بعد تعبئة الحقول
    buildLocationOptions(countryName, stateName, cityName);
  }, [store, locations, businessActivities]);

  const fetchStore = async () => {
    setLoading(true);
    const token = localStorage.getItem("user-token");
    try {
      const response = await fetch(BASE_URL + "/manager/profile", {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json();
      const s = data.data?.store || {};
      setStore(s);
      setForm({
        name_ar: s.name_ar || "",
        name_en: s.name_en || "",
        email: s.email || "",
        business_activity: s.business_activity || "",
        market: s.market || "",
        country: s.country || "",
        state: s.state || "",
        city: s.city || "",
        logo: s.logo || "",
        image: s.image || "",
        location: s.location || "",
        street: s.street || "",
        zip_code: s.zip_code || "",
        subcode: s.subcode || "",
      });
      // منطق عرض الشعار
      if (s.logo && typeof s.logo === "string") {
        setLogoPreview(
          s.logo.startsWith("http")
            ? s.logo
            : `https://back.al-balad.sa/uploads/stores/logos/${s.logo}`
        );
      } else {
        setLogoPreview("");
      }
      // صورة المتجر
      if (s.image && typeof s.image === "string") {
        setImagePreview(
          s.image.startsWith("http")
            ? s.image
            : `https://back.al-balad.sa/uploads/stores/images/${s.image}`
        );
      } else {
        setImagePreview("");
      }
    } catch (error) {
      setStore(null);
    } finally {
      setLoading(false);
    }
  };

  const fetchLocations = async () => {
    try {
      const res = await fetch(BASE_URL + "/stores/create");
      const data = await res.json();
      setLocations(data.data?.locations || []);
      setBusinessActivities(data.data?.business_activities || []);
    } catch (e) {
      setLocations([]);
      setBusinessActivities([]);
    }
  };

  // تحديث الخيارات عند تغيير الدولة/الولاية/المدينة
  useEffect(() => {
    // الدولة
    const countryObj = locations.find((c) => c.name === form.country);
    setStatesOptions(countryObj?.childrens || []);
    if (!countryObj || !countryObj.childrens.some((s) => s.name === form.state)) {
      setForm((prev) => ({ ...prev, state: "", city: "", market: "" }));
      setCitiesOptions([]);
      setMarketsOptions([]);
      return;
    }
    // الولاية
    const stateObj = countryObj.childrens.find((s) => s.name === form.state);
    setCitiesOptions(stateObj?.childrens || []);
    if (!stateObj || !stateObj.childrens.some((ct) => ct.name === form.city)) {
      setForm((prev) => ({ ...prev, city: "", market: "" }));
      setMarketsOptions([]);
      return;
    }
    // المدينة
    const cityObj = stateObj.childrens.find((ct) => ct.name === form.city);
    setMarketsOptions(cityObj?.markets || []);
    if (!cityObj || !cityObj.markets.some((m) => m.name === form.market)) {
      setForm((prev) => ({ ...prev, market: "" }));
    }
  }, [form.country, form.state, form.city, locations]);

  const validateForm = (fields = form) => {
    const newErrors = {};
    // logo
    if (!fields.logo || (typeof fields.logo === "string" && !fields.logo.trim()))
      newErrors.logo = "شعار المتجر مطلوب";
    // صورة المتجر
    if (!fields.image || (typeof fields.image === "string" && !fields.image.trim()))
      newErrors.image = "صورة المتجر مطلوبة";
    if (!fields.name_ar.trim()) newErrors.name_ar = "اسم المتجر بالعربي مطلوب";
    if (!fields.name_en.trim()) newErrors.name_en = "اسم المتجر بالإنجليزي مطلوب";
    if (!fields.email.trim()) {
      newErrors.email = "الإيميل مطلوب";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(fields.email)) {
      newErrors.email = "صيغة البريد الإلكتروني غير صحيحة";
    }
    if (!fields.phone || !fields.phone.trim()) newErrors.phone = "رقم الهاتف مطلوب";
    if (!fields.business_activity) newErrors.business_activity = "نشاط المتجر مطلوب";
    if (!fields.market) newErrors.market = "السوق مطلوب";
    if (!fields.country) newErrors.country = "الدولة مطلوبة";
    if (!fields.state) newErrors.state = "الولاية / المنطقة مطلوبة";
    if (!fields.city) newErrors.city = "المدينة / المحافظة مطلوبة";
    if (!fields.location || !fields.location.trim()) {
      newErrors.location = "موقع المتجر مطلوب";
    } else if (!(fields.location.includes("<iframe") && fields.location.includes("</iframe>"))) {
      newErrors.location = "يجب أن يكون الموقع عبارة عن كود iframe";
    }
    if (!fields.zip_code.trim()) newErrors.zip_code = "الرمز البريدي مطلوب";
    if (!fields.street.trim()) newErrors.street = "الشارع مطلوب";
    return newErrors;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    setTouched((prev) => ({ ...prev, [name]: true }));
    setErrors(validateForm({ ...form, [name]: value }));
  };

  const handleAutoChange = (name, value) => {
    setForm((prev) => ({ ...prev, [name]: value || "" }));
    setTouched((prev) => ({ ...prev, [name]: true }));
    setErrors(validateForm({ ...form, [name]: value || "" }));
  };

  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setForm((prev) => ({ ...prev, logo: file }));
      setLogoPreview(URL.createObjectURL(file));
      setTouched((prev) => ({ ...prev, logo: true }));
      setErrors(validateForm({ ...form, logo: file }));
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setForm((prev) => ({ ...prev, image: file }));
      setImagePreview(URL.createObjectURL(file));
      setTouched((prev) => ({ ...prev, image: true }));
      setErrors(validateForm({ ...form, image: file }));
    }
  };

  const handleBlur = (e) => {
    const { name } = e.target;
    setTouched((prev) => ({ ...prev, [name]: true }));
    setErrors(validateForm(form));
  };

  const isFormValid = Object.keys(validateForm(form)).length === 0;

  // التحقق من صحة iframe
  const isValidIframe = (str) => str && str.includes("<iframe") && str.includes("</iframe>");

  // دالة استخراج id المدينة المختارة
  const getSelectedCityId = () => {
    const countryObj = locations.find((c) => c.name === form.country);
    const stateObj = countryObj?.childrens?.find((s) => s.name === form.state);
    const cityObj = stateObj?.childrens?.find((ct) => ct.name === form.city);
    return cityObj?.id || null;
  };

  // دالة استخراج id السوق المختار
  const getSelectedMarketId = () => {
    const countryObj = locations.find((c) => c.name === form.country);
    const stateObj = countryObj?.childrens?.find((s) => s.name === form.state);
    const cityObj = stateObj?.childrens?.find((ct) => ct.name === form.city);
    const marketObj = cityObj?.markets?.find((m) => m.name === form.market);
    return marketObj?.id || null;
  };

  // دالة استخراج id النشاط التجاري المختار
  const getSelectedBusinessActivityId = () => {
    const actObj = businessActivities.find((a) => a.name === form.business_activity);
    return actObj?.id || null;
  };

  // عند الضغط على زر الحفظ
  const handleSave = async () => {
    const validationErrors = validateForm(form);
    setErrors(validationErrors);
    setTouched({
      name_ar: true,
      name_en: true,
      email: true,
      phone: true,
      logo: true,
      image: true,
      business_activity: true,
      market: true,
      country: true,
      state: true,
      city: true,
      location: true,
      street: true,
      zip_code: true,
    });
    if (Object.keys(validationErrors).length > 0) return;
    try {
      const formPayload = new FormData();
      formPayload.append("name_ar", form.name_ar);
      formPayload.append("name_en", form.name_en);
      formPayload.append("email", form.email);
      formPayload.append("phone", form.phone);
      formPayload.append("location", form.location);
      formPayload.append("street", form.street);
      formPayload.append("zip_code", form.zip_code);
      formPayload.append("subcode", form.subcode);
      formPayload.append("location_id", getSelectedCityId());
      formPayload.append("market_id", getSelectedMarketId());
      formPayload.append("business_activitie_id", getSelectedBusinessActivityId());
      if (form.logo instanceof File) formPayload.append("logo", form.logo);
      if (form.image instanceof File) formPayload.append("image", form.image);
      const token = localStorage.getItem("user-token");
      Swal.fire({
        title: "جاري حفظ البيانات...",
        allowOutsideClick: false,
        didOpen: () => {
          Swal.showLoading();
          const container = document.querySelector(".swal2-container");
          if (container) container.style.zIndex = "99999";
        },
      });
      const response = await axios.post(
        BASE_URL + "/manager/stores/update",
        formPayload,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${token}`,
          },
        }
      );
      Swal.close();
      if (response.data.status) {
        await Swal.fire({
          icon: "success",
          title: "تم الحفظ!",
          text: "تم حفظ بيانات المتجر بنجاح.",
          confirmButtonText: "موافق",
          didOpen: () => {
            const container = document.querySelector(".swal2-container");
            if (container) container.style.zIndex = "99999";
          },
        });
        if (onClose) onClose();
      } else {
        throw new Error(response.data.message || "حدث خطأ غير متوقع");
      }
    } catch (error) {
      Swal.close();
      const errorMessage =
        error.response?.data?.message || error.message || "حدث خطأ أثناء حفظ البيانات";
      Swal.fire({
        icon: "error",
        title: "فشل الحفظ!",
        text: errorMessage,
        confirmButtonText: "موافق",
        didOpen: () => {
          const container = document.querySelector(".swal2-container");
          if (container) container.style.zIndex = "99999";
        },
      });
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>تعديل بيانات المتجر</DialogTitle>
      <DialogContent>
        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
            <CircularProgress />
          </Box>
        ) : (
          <Box component="form" display="flex" flexDirection="column" gap={3} mt={1}>
            <Grid container spacing={2}>
              {/* الشعار وصورة المتجر */}
              <Grid item xs={12} md={12} display="flex" justifyContent="center" alignItems="center">
                <Box display="flex" flexDirection="row" alignItems="center" gap={6}>
                  {/* شعار المتجر */}
                  <Box display="flex" flexDirection="column" alignItems="center" gap={1}>
                    <Avatar src={logoPreview} sx={{ width: 100, height: 100, mb: 1 }} />
                    <input
                      accept="image/*"
                      style={{ display: "none" }}
                      id="logo-upload"
                      type="file"
                      onChange={handleLogoChange}
                    />
                    <label htmlFor="logo-upload">
                      <IconButton color="primary" component="span">
                        <PhotoCamera />
                      </IconButton>
                    </label>
                    <Box fontSize={13} mt={1} color="error.main">
                      {touched.logo && errors.logo ? errors.logo : ""}
                    </Box>
                    <Box fontSize={12} color="text.secondary">
                      شعار المتجر
                    </Box>
                  </Box>
                  {/* صورة المتجر */}
                  <Box display="flex" flexDirection="column" alignItems="center" gap={1}>
                    <Avatar
                      src={imagePreview}
                      sx={{ width: 100, height: 100, mb: 1, border: "2px solid #1976d2" }}
                    />
                    <input
                      accept="image/*"
                      style={{ display: "none" }}
                      id="image-upload"
                      type="file"
                      onChange={handleImageChange}
                    />
                    <label htmlFor="image-upload">
                      <IconButton color="primary" component="span">
                        <PhotoCamera />
                      </IconButton>
                    </label>
                    <Box fontSize={13} mt={1} color="error.main">
                      {touched.image && errors.image ? errors.image : ""}
                    </Box>
                    <Box fontSize={12} color="text.secondary">
                      صورة المتجر <span style={{ color: "red" }}>*</span>
                    </Box>
                  </Box>
                </Box>
              </Grid>
              {/* الاسم بالعربي والإنجليزي */}
              <Grid item xs={12} md={6}>
                <TextField
                  label={
                    <span>
                      اسم المتجر بالعربي <span style={{ color: "red" }}>*</span>
                    </span>
                  }
                  name="name_ar"
                  value={form.name_ar}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  fullWidth
                  variant="outlined"
                  error={!!errors.name_ar && touched.name_ar}
                  helperText={touched.name_ar && errors.name_ar ? errors.name_ar : ""}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label={
                    <span>
                      اسم المتجر بالإنجليزي <span style={{ color: "red" }}>*</span>
                    </span>
                  }
                  name="name_en"
                  value={form.name_en}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  fullWidth
                  variant="outlined"
                  error={!!errors.name_en && touched.name_en}
                  helperText={touched.name_en && errors.name_en ? errors.name_en : ""}
                />
              </Grid>
              {/* إيميل المتجر */}
              <Grid item xs={12} md={6}>
                <TextField
                  label={
                    <span>
                      إيميل المتجر <span style={{ color: "red" }}>*</span>
                    </span>
                  }
                  name="email"
                  value={form.email}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  fullWidth
                  variant="outlined"
                  error={!!errors.email && touched.email}
                  helperText={touched.email && errors.email ? errors.email : ""}
                />
              </Grid>
              {/* رقم الهاتف */}
              <Grid item xs={12} md={6}>
                <TextField
                  label={
                    <span>
                      رقم الهاتف <span style={{ color: "red" }}>*</span>
                    </span>
                  }
                  name="phone"
                  value={form.phone}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  fullWidth
                  variant="outlined"
                  error={!!errors.phone && touched.phone}
                  helperText={touched.phone && errors.phone ? errors.phone : ""}
                />
              </Grid>
              {/* نشاط المتجر */}
              <Grid item xs={12} md={6}>
                <Autocomplete
                  options={businessActivities.map((a) => a.name)}
                  value={form.business_activity}
                  onChange={(_, newValue) => handleAutoChange("business_activity", newValue)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={
                        <span>
                          نشاط المتجر <span style={{ color: "red" }}>*</span>
                        </span>
                      }
                      variant="outlined"
                      fullWidth
                      error={!!errors.business_activity && touched.business_activity}
                      helperText={
                        touched.business_activity && errors.business_activity
                          ? errors.business_activity
                          : ""
                      }
                      onBlur={handleBlur}
                    />
                  )}
                />
              </Grid>
              {/* الدولة */}
              <Grid item xs={12} md={6}>
                <Autocomplete
                  options={locations.map((c) => c.name)}
                  value={form.country}
                  onChange={(_, newValue) => handleAutoChange("country", newValue)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={
                        <span>
                          الدولة <span style={{ color: "red" }}>*</span>
                        </span>
                      }
                      variant="outlined"
                      fullWidth
                      error={!!errors.country && touched.country}
                      helperText={touched.country && errors.country ? errors.country : ""}
                      onBlur={handleBlur}
                    />
                  )}
                />
              </Grid>
              {/* الولاية / المنطقة */}
              <Grid item xs={12} md={6}>
                <Autocomplete
                  options={statesOptions.map((s) => s.name)}
                  value={form.state}
                  onChange={(_, newValue) => handleAutoChange("state", newValue)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={
                        <span>
                          الولاية / المنطقة <span style={{ color: "red" }}>*</span>
                        </span>
                      }
                      variant="outlined"
                      fullWidth
                      error={!!errors.state && touched.state}
                      helperText={touched.state && errors.state ? errors.state : ""}
                      onBlur={handleBlur}
                    />
                  )}
                  disabled={!form.country}
                />
              </Grid>
              {/* المدينة / المحافظة */}
              <Grid item xs={12} md={6}>
                <Autocomplete
                  options={citiesOptions.map((ct) => ct.name)}
                  value={form.city}
                  onChange={(_, newValue) => handleAutoChange("city", newValue)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={
                        <span>
                          المدينة / المحافظة <span style={{ color: "red" }}>*</span>
                        </span>
                      }
                      variant="outlined"
                      fullWidth
                      error={!!errors.city && touched.city}
                      helperText={touched.city && errors.city ? errors.city : ""}
                      onBlur={handleBlur}
                    />
                  )}
                  disabled={!form.state}
                />
              </Grid>
              {/* السوق */}
              <Grid item xs={12} md={6}>
                <Autocomplete
                  options={marketsOptions.map((m) => m.name)}
                  value={form.market}
                  onChange={(_, newValue) => handleAutoChange("market", newValue)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={
                        <span>
                          السوق <span style={{ color: "red" }}>*</span>
                        </span>
                      }
                      variant="outlined"
                      fullWidth
                      error={!!errors.market && touched.market}
                      helperText={touched.market && errors.market ? errors.market : ""}
                      onBlur={handleBlur}
                    />
                  )}
                  disabled={!form.city}
                />
              </Grid>
              {/* الموقع في الخريطة */}
              <Grid item xs={12}>
                <TextField
                  label={
                    <span>
                      موقع المتجر (iframe) <span style={{ color: "red" }}>*</span>
                    </span>
                  }
                  name="location"
                  value={form.location}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  fullWidth
                  variant="outlined"
                  multiline
                  minRows={2}
                  placeholder="ضع كود iframe هنا..."
                  error={!!errors.location && touched.location}
                  helperText={touched.location && errors.location ? errors.location : ""}
                />
                {isValidIframe(form.location) && (
                  <Box mt={2} sx={{ borderRadius: 2, overflow: "hidden", boxShadow: 2 }}>
                    <div
                      style={{ width: "100%" }}
                      dangerouslySetInnerHTML={{
                        __html: form.location
                          .replace('width="600"', 'width="100%"')
                          .replace('height="450"', 'height="300"')
                          .replace(
                            'style="border:0;"',
                            'style="border:0;width:100%;height:300px;"'
                          ),
                      }}
                    />
                  </Box>
                )}
              </Grid>
              {/* الشارع، الرمز البريدي، subcode */}
              <Grid item xs={12} md={4}>
                <TextField
                  label={
                    <span>
                      الشارع <span style={{ color: "red" }}>*</span>
                    </span>
                  }
                  name="street"
                  value={form.street}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  fullWidth
                  variant="outlined"
                  error={!!errors.street && touched.street}
                  helperText={touched.street && errors.street ? errors.street : ""}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  label={
                    <span>
                      الرمز البريدي <span style={{ color: "red" }}>*</span>
                    </span>
                  }
                  name="zip_code"
                  value={form.zip_code}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  fullWidth
                  variant="outlined"
                  error={!!errors.zip_code && touched.zip_code}
                  helperText={touched.zip_code && errors.zip_code ? errors.zip_code : ""}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  label="Subcode"
                  name="subcode"
                  value={form.subcode}
                  onChange={handleChange}
                  fullWidth
                  variant="outlined"
                />
              </Grid>
            </Grid>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button
          onClick={onClose}
          color="error"
          variant="outlined"
          sx={{ color: "white !important", backgroundColor: "red !important", border: "none" }}
        >
          إلغاء
        </Button>
        <Button
          color="primary"
          variant="contained"
          disabled={!isFormValid}
          sx={{ color: "white !important" }}
          onClick={handleSave}
        >
          حفظ التعديلات
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default StoreInfoEdit;
